import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface NCRData {
  qcInspectionId: number | null;
  panelId: number | null;
  productId: number | null;
  ncrNumber: string;
  dateIdentified: Date;
  identifiedBy: number | null;
  defectType: string;
  defectCategory: "minor" | "major" | "critical";
  defectDescription: string;
  rootCause: string;
  impactAssessment: string;
  proposedDisposition: "repair" | "reject" | "use_as_is" | "rework";
  dispositionRationale: string;
  correctiveAction: string;
  preventiveAction: string;
  assignedTo: number | null;
  targetCompletionDate: Date | null;
  photos: File[];
  attachments: File[];
  approvalStatus: "pending" | "approved" | "rejected";
  approvedBy: number | null;
  approvalDate: Date | null;
  approvalNotes: string;
}

class NCRWorkflowStore {
  rootStore: RootStore;

  // Observables
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  ncrData: NCRData = {
    qcInspectionId: null,
    panelId: null,
    productId: null,
    ncrNumber: "",
    dateIdentified: new Date(),
    identifiedBy: null,
    defectType: "",
    defectCategory: "major",
    defectDescription: "",
    rootCause: "",
    impactAssessment: "",
    proposedDisposition: "repair",
    dispositionRationale: "",
    correctiveAction: "",
    preventiveAction: "",
    assignedTo: null,
    targetCompletionDate: null,
    photos: [],
    attachments: [],
    approvalStatus: "pending",
    approvedBy: null,
    approvalDate: null,
    approvalNotes: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  updateNCRData = <K extends keyof NCRData>(
    field: K,
    value: NCRData[K]
  ) => {
    this.ncrData[field] = value;
  };

  selectQCInspection = (qcId: number) => {
    const qcStore = this.rootStore.qcStore as ServiceStore;
    const qc = qcStore?.entities.find((q: any) => q.id === qcId);

    runInAction(() => {
      this.ncrData.qcInspectionId = qcId;
      this.ncrData.panelId = (qc?.productionUnitId as number) || null;
      this.ncrData.productId = (qc?.productId as number) || null;
    });
  };

  generateNCRNumber = (): string => {
    return `NCR-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
  };

  addPhotos = (files: FileList) => {
    const newFiles = Array.from(files);
    this.ncrData.photos = [...this.ncrData.photos, ...newFiles];
  };

  removePhoto = (index: number) => {
    this.ncrData.photos = this.ncrData.photos.filter((_, i) => i !== index);
  };

  addAttachments = (files: FileList) => {
    const newFiles = Array.from(files);
    this.ncrData.attachments = [...this.ncrData.attachments, ...newFiles];
  };

  removeAttachment = (index: number) => {
    this.ncrData.attachments = this.ncrData.attachments.filter((_, i) => i !== index);
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // QC Inspection Selection
        if (!this.ncrData.qcInspectionId) {
          this.setError("Please select a QC inspection");
          return false;
        }
        if (!this.ncrData.identifiedBy) {
          this.setError("Please select who identified the issue");
          return false;
        }
        break;

      case 1: // Defect Details
        if (!this.ncrData.defectType) {
          this.setError("Please specify the defect type");
          return false;
        }
        if (!this.ncrData.defectDescription) {
          this.setError("Please provide a defect description");
          return false;
        }
        if (!this.ncrData.rootCause) {
          this.setError("Please identify the root cause");
          return false;
        }
        break;

      case 2: // Disposition & Action
        if (!this.ncrData.dispositionRationale) {
          this.setError("Please provide disposition rationale");
          return false;
        }
        if (!this.ncrData.correctiveAction) {
          this.setError("Please specify corrective action");
          return false;
        }
        if (!this.ncrData.assignedTo) {
          this.setError("Please assign the NCR to someone");
          return false;
        }
        break;

      case 3: // Review & Submit
        // All validation already done in previous steps
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const qcStore = this.rootStore.qcStore as ServiceStore;
      const panelStore = this.rootStore.panelStore as ServiceStore;
      const productsStore = this.rootStore.productsStore as ServiceStore;
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const userStore = this.rootStore.userStore as ServiceStore;

      await Promise.all([
        qcStore?.find({
          query: {
            status: { $in: ["fail", "rework"] },
            $limit: 100,
            $sort: { inspectionDateTime: -1 }
          }
        }),
        panelStore?.find({ query: { qcStatus: { $in: ["failed", "rework"] } } }),
        productsStore?.find(),
        jobStore?.find({ query: { archived: false } }),
        userStore?.find(),
      ]);

      // Generate NCR number
      const ncrNumber = this.generateNCRNumber();
      runInAction(() => {
        this.updateNCRData("ncrNumber", ncrNumber);
      });
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  submitNCR = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const holdStore = this.rootStore.holdStore as ServiceStore;
      const panelStore = this.rootStore.panelStore as ServiceStore;

      // Create hold record for the panel
      if (this.ncrData.panelId) {
        await holdStore?.create({
          panelId: this.ncrData.panelId,
          productId: this.ncrData.productId,
          holdType: "quality",
          reason: this.ncrData.defectDescription,
          severity: this.ncrData.defectCategory,
          disposition: this.ncrData.proposedDisposition,
          placedBy: this.ncrData.identifiedBy,
          placedDate: new Date().toISOString(),
          status: "active",
          ncrNumber: this.ncrData.ncrNumber,
        });

        // Update panel status
        await panelStore?.patch(this.ncrData.panelId, {
          status: "Hold",
          qcStatus: "failed",
          holdReason: this.ncrData.defectDescription,
        });
      }

      // TODO: Create NCR record when NCR service is available
      // await ncrStore?.create({
      //   ...this.ncrData,
      //   dateIdentified: this.ncrData.dateIdentified.toISOString(),
      //   targetCompletionDate: this.ncrData.targetCompletionDate?.toISOString(),
      // });

      runInAction(() => {
        this.setSuccess("Non-Conformance Report created successfully!");
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to create NCR");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.ncrData = {
      qcInspectionId: null,
      panelId: null,
      productId: null,
      ncrNumber: this.generateNCRNumber(),
      dateIdentified: new Date(),
      identifiedBy: null,
      defectType: "",
      defectCategory: "major",
      defectDescription: "",
      rootCause: "",
      impactAssessment: "",
      proposedDisposition: "repair",
      dispositionRationale: "",
      correctiveAction: "",
      preventiveAction: "",
      assignedTo: null,
      targetCompletionDate: null,
      photos: [],
      attachments: [],
      approvalStatus: "pending",
      approvedBy: null,
      approvalDate: null,
      approvalNotes: "",
    };
  };

  // Computed values
  get failedQCInspections() {
    const qcStore = this.rootStore.qcStore as ServiceStore;
    return qcStore?.entities.filter(
      (qc: any) => qc.status === "fail" || qc.status === "rework"
    ) || [];
  }

  get panels() {
    const panelStore = this.rootStore.panelStore as ServiceStore;
    return panelStore?.entities || [];
  }

  get products() {
    const productsStore = this.rootStore.productsStore as ServiceStore;
    return productsStore?.entities || [];
  }

  get users() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities || [];
  }

  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get selectedQC() {
    if (!this.ncrData.qcInspectionId) return null;
    const qcStore = this.rootStore.qcStore as ServiceStore;
    return qcStore?.entities.find((q: any) => q.id === this.ncrData.qcInspectionId);
  }

  get selectedPanel() {
    if (!this.ncrData.panelId) return null;
    return this.panels.find((p: any) => p.id === this.ncrData.panelId);
  }

  get selectedProduct() {
    if (!this.ncrData.productId) return null;
    return this.products.find((p: any) => p.id === this.ncrData.productId);
  }

  get selectedUser() {
    if (!this.ncrData.identifiedBy) return null;
    return this.users.find((u: any) => u.id === this.ncrData.identifiedBy);
  }

  get assignedUser() {
    if (!this.ncrData.assignedTo) return null;
    return this.users.find((u: any) => u.id === this.ncrData.assignedTo);
  }

  get canProceedFromStep1() {
    return !!this.ncrData.qcInspectionId && !!this.ncrData.identifiedBy;
  }

  get canProceedFromStep2() {
    return !!this.ncrData.defectType && !!this.ncrData.defectDescription && !!this.ncrData.rootCause;
  }

  get canProceedFromStep3() {
    return !!this.ncrData.dispositionRationale && !!this.ncrData.correctiveAction && !!this.ncrData.assignedTo;
  }

  get defectSeverityColor() {
    switch (this.ncrData.defectCategory) {
      case "critical":
        return "error";
      case "major":
        return "warning";
      case "minor":
        return "info";
      default:
        return "default";
    }
  }

  get dispositionDisplayName() {
    return this.ncrData.proposedDisposition.replace(/_/g, " ").toUpperCase();
  }
}

export default NCRWorkflowStore;